import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, LogOut, RefreshCw, Inbox, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (d) => {
  if (d == null) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(d);
};

const inputCls =
  "w-full bg-white border border-slate-200 px-5 py-4 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-slate-900 transition-colors duration-300";

const pkgColor = {
  Wanderlust: "bg-slate-100 text-slate-700",
  Venture: "bg-slate-900 text-white",
  Odyssey: "bg-[#3A94BE]/10 text-[#2a6d8f]",
};

export const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("td_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setEnquiries(data.enquiries);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("td_admin_token");
        setToken(null);
        setEnquiries(null);
      } else {
        toast.error(formatDetail(e.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("td_admin_token", data.token);
      setToken(data.token);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("td_admin_token");
    setToken(null);
    setEnquiries(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form
          onSubmit={login}
          className="w-full max-w-md bg-white border border-slate-200 p-10"
          data-testid="admin-login-form"
        >
          <p className="font-display text-3xl font-semibold tracking-tight text-slate-900">
            TrioDocz<span className="text-slate-400">.</span>
          </p>
          <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mt-2 mb-10">Enquiry dashboard</p>
          <label htmlFor="admin-email" className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-slate-500 mb-2.5">
            Email
          </label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@triodocz.com"
            className={inputCls}
          />
          <label htmlFor="admin-password" className="block text-[11px] font-semibold tracking-[0.2em] uppercase text-slate-500 mt-6 mb-2.5">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={inputCls}
          />
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="group mt-8 flex items-center justify-center gap-3 w-full h-[52px] bg-slate-900 text-white text-sm font-semibold rounded-full hover:bg-slate-700 disabled:opacity-60 transition-colors duration-300"
          >
            {loading ? "Signing in…" : "Sign in"}
            {!loading && <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />}
          </button>
          <a href="/" className="block mt-6 text-xs text-slate-400 hover:text-slate-600 transition-colors duration-300">
            ← Back to triodocz.com
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] px-6 md:px-12 py-12" data-testid="admin-dashboard">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-6 pb-8 border-b border-slate-200">
          <div>
            <p className="font-display text-3xl sm:text-4xl tracking-tight text-slate-900">
              Enquiries<span className="text-slate-400">.</span>
            </p>
            <p className="text-sm text-slate-500 mt-2">
              {enquiries ? `${enquiries.length} received` : "Loading…"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              data-testid="admin-view-site-link"
              className="flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-slate-500 border border-slate-300 px-5 py-3 rounded-full hover:border-slate-900 hover:text-slate-900 transition-colors duration-300"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Site
            </a>
            <button
              data-testid="admin-refresh-button"
              onClick={() => load(token)}
              className="flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-slate-500 border border-slate-300 px-5 py-3 rounded-full hover:border-slate-900 hover:text-slate-900 transition-colors duration-300"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-white bg-slate-900 px-5 py-3 rounded-full hover:bg-slate-700 transition-colors duration-300"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>

        {enquiries && enquiries.length === 0 && (
          <div className="py-24 flex flex-col items-center text-center" data-testid="enquiries-empty">
            <Inbox className="w-10 h-10 text-slate-300" />
            <p className="font-display text-2xl text-slate-900 mt-6">No enquiries yet</p>
            <p className="text-sm text-slate-500 mt-2">New contact form submissions will appear here.</p>
          </div>
        )}

        <div className="divide-y divide-slate-200">
          {enquiries?.map((q) => (
            <div key={q.id} className="py-8 grid md:grid-cols-12 gap-4" data-testid="enquiry-row">
              <div className="md:col-span-3">
                <p className="font-semibold text-slate-900">{q.name}</p>
                <a href={`mailto:${q.email}`} className="text-sm text-[#2a6d8f] hover:underline">{q.email}</a>
                <p className="text-xs text-slate-400 mt-1.5">
                  {new Date(q.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <div className="md:col-span-2">
                <span className={`inline-block text-[11px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full ${pkgColor[q.package] || "bg-slate-100 text-slate-500"}`}>
                  {q.package}
                </span>
              </div>
              <p className="md:col-span-7 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{q.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
